import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { environment } from 'src/environments/environment';

// Two engines, picked by whether a Maps key is configured.
//
// Google Maps is preferred because its POI database carries the local businesses
// authors actually navigate by ("Al Madina Sweet", "Burhan Pharmacy"), which OSM
// simply does not have in most of Pakistan and Saudi. It needs a paid key.
//
// Leaflet + OpenStreetMap is the keyless fallback so the picker still works on
// any environment where the key has not been set. Both are loaded from CDN on
// first use — the same way index.html already pulls bootstrap and chart.js — so
// the bundle carries neither and the cost is paid only once a geolocation level
// is actually authored.
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const LEAFLET_IMAGES = 'https://unpkg.com/leaflet@1.9.4/dist/images/';

// Falls back to Riyadh when a point has no coordinates yet. Zoom 12 matches the
// admin portal's empty state — a city view rather than a whole country.
const FALLBACK = { lat: 24.7136, lng: 46.6753, zoom: 12 };

// Shared across every picker on the page so N points cost one script load.
let leafletPromise: Promise<void> | undefined;
let googlePromise: Promise<void> | undefined;

function loadGoogleMaps(key: string): Promise<void> {
  if ((window as any).google?.maps) {
    return Promise.resolve();
  }
  if (googlePromise) {
    return googlePromise;
  }
  googlePromise = new Promise<void>((resolve, reject) => {
    // With loading=async the script tag bootstraps a loader, so `onload` fires
    // before google.maps exists. The callback param is the only signal that the
    // API is genuinely ready to construct a Map.
    const callbackName = '__luqtaGoogleMapsReady';
    (window as any)[callbackName] = () => {
      delete (window as any)[callbackName];
      resolve();
    };
    const script = document.createElement('script');
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}` +
      `&libraries=places&loading=async&callback=${callbackName}`;
    script.async = true;
    script.onerror = () => {
      delete (window as any)[callbackName];
      // Drop the rejected promise so a later picker retries rather than
      // inheriting a permanent failure.
      googlePromise = undefined;
      reject(new Error('google-maps-load-failed'));
    };
    document.head.appendChild(script);
  });
  return googlePromise;
}

function loadLeaflet(): Promise<void> {
  if ((window as any).L) {
    return Promise.resolve();
  }
  if (leafletPromise) {
    return leafletPromise;
  }
  leafletPromise = new Promise<void>((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.onload = () => resolve();
    script.onerror = () => {
      // Drop the rejected promise so the next picker retries instead of
      // inheriting a permanent failure.
      leafletPromise = undefined;
      reject(new Error('leaflet-load-failed'));
    };
    document.body.appendChild(script);
  });
  return leafletPromise;
}

interface PlaceResult {
  label: string;
  lat: number;
  lng: number;
}

@Component({
  selector: 'app-geo-map-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './geo-map-picker.component.html',
  styleUrls: ['./geo-map-picker.component.scss'],
})
export class GeoMapPickerComponent implements AfterViewInit, OnDestroy {
  /** The point FormGroup this picker writes lat/lng/location_name/radius into. */
  @Input({ required: true }) point!: FormGroup;

  @ViewChild('mapHost', { static: true }) mapHost!: ElementRef<HTMLDivElement>;
  @ViewChild('mapShell', { static: true }) mapShell!: ElementRef<HTMLDivElement>;

  private hostEl = inject(ElementRef<HTMLElement>);

  isLoading = true;
  hasFailed = false;
  isGeocoding = false;
  isSearching = false;
  isSatellite = false;
  isFullscreen = false;
  isResolvingUrl = false;
  isLocating = false;
  searchTerm = '';
  /** i18n key, shown under the search box when a pasted URL can't be resolved. */
  searchError = '';
  results: PlaceResult[] = [];

  private map: any = null;
  private marker: any = null;
  private radiusCircle: any = null;
  private prizeCircle: any = null;
  private streetLayer: any = null;
  private satelliteLayer: any = null;
  private hybridLayer: any = null;
  private terrainLayer: any = null;
  /** Latches so a burst of tile errors only swaps the base layer once. */
  private usedTileFallback = false;
  private valueSub?: Subscription;
  private searchSub?: Subscription;
  private search$ = new Subject<string>();

  /** True once a Google map is actually running. Deliberately not derived from
   *  the key alone: if the Google script fails to load we fall back to Leaflet,
   *  and every engine-specific call has to follow that fallback. */
  private isGoogle = false;
  private geocoder: any = null;
  private readonly googleKey: string = (environment as any)?.googleMapsApiKey || '';

  private fullscreenHandler = () => {
    this.isFullscreen = !!document.fullscreenElement;
    // The container just changed size; both engines cache dimensions on create.
    setTimeout(() => this.refreshMapSize(), 120);
  };

  get radiusValue(): number {
    return Number(this.point?.get('radius')?.value) || 0;
  }

  get prizeRadiusValue(): number {
    return Number(this.point?.get('prize_radius')?.value) || 0;
  }

  async ngAfterViewInit(): Promise<void> {
    this.searchSub = this.search$
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((term) => this.runSearch(term));

    // Google first when a key exists; fall back to Leaflet if it cannot load, so
    // a bad key or blocked request degrades instead of leaving an empty box.
    if (this.googleKey) {
      try {
        await loadGoogleMaps(this.googleKey);
        this.isGoogle = true;
        this.initGoogleMap();
        document.addEventListener('fullscreenchange', this.fullscreenHandler);
        return;
      } catch {
        this.isGoogle = false;
      }
    }

    try {
      await loadLeaflet();
    } catch {
      this.isLoading = false;
      this.hasFailed = true;
      return;
    }
    this.initMap();
    document.addEventListener('fullscreenchange', this.fullscreenHandler);
  }

  ngOnDestroy(): void {
    document.removeEventListener('fullscreenchange', this.fullscreenHandler);
    this.valueSub?.unsubscribe();
    this.searchSub?.unsubscribe();
    // Google maps are torn down by dropping the reference; Leaflet needs remove().
    if (!this.isGoogle) {
      this.map?.remove();
    }
    this.map = null;
  }

  // ── Engine adapters ──
  // The form-writing logic below is engine-agnostic, so every map call it makes
  // goes through these four instead of Leaflet's API directly.

  private moveMarker(lat: number, lng: number): void {
    if (!this.marker) {
      return;
    }
    if (this.isGoogle) {
      this.marker.setPosition({ lat, lng });
    } else {
      this.marker.setLatLng([lat, lng]);
    }
  }

  private centreMap(lat: number, lng: number, zoom?: number): void {
    if (!this.map) {
      return;
    }
    if (this.isGoogle) {
      this.map.setCenter({ lat, lng });
      if (zoom != null) {
        this.map.setZoom(zoom);
      }
    } else if (zoom != null) {
      this.map.setView([lat, lng], zoom);
    } else {
      this.map.panTo([lat, lng], { animate: false });
    }
  }

  private refreshMapSize(): void {
    if (!this.map || this.isGoogle) {
      // Google re-measures itself on container resize.
      return;
    }
    this.map.invalidateSize();
  }

  private markerPosition(): { lat: number; lng: number } | null {
    if (!this.marker) {
      return null;
    }
    if (this.isGoogle) {
      const pos = this.marker.getPosition?.();
      return pos ? { lat: pos.lat(), lng: pos.lng() } : null;
    }
    const pos = this.marker.getLatLng();
    return pos ? { lat: pos.lat, lng: pos.lng } : null;
  }

  private initGoogleMap(): void {
    const google = (window as any).google;
    if (!google?.maps || !this.mapHost?.nativeElement) {
      this.isLoading = false;
      return;
    }

    const lat = this.point.get('lat')?.value;
    const lng = this.point.get('lng')?.value;
    const hasCoords =
      lat != null && lng != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng));
    const centre = hasCoords
      ? { lat: Number(lat), lng: Number(lng) }
      : { lat: FALLBACK.lat, lng: FALLBACK.lng };

    this.map = new google.maps.Map(this.mapHost.nativeElement, {
      center: centre,
      zoom: hasCoords ? 18 : FALLBACK.zoom,
      // The overlay controls in the template already cover these, and Google's
      // own buttons would sit underneath them.
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      clickableIcons: false,
    });
    this.geocoder = new google.maps.Geocoder();

    this.radiusCircle = new google.maps.Circle({
      map: this.map, center: centre, radius: 1,
      strokeColor: '#5304fb', strokeWeight: 3, fillColor: '#5304fb', fillOpacity: 0.18,
    });
    this.prizeCircle = new google.maps.Circle({
      map: this.map, center: centre, radius: 1,
      strokeColor: '#f59e0b', strokeWeight: 2, fillColor: '#f59e0b', fillOpacity: 0.25,
    });
    this.marker = new google.maps.Marker({ map: this.map, position: centre, draggable: true });
    this.redrawCircles();

    this.marker.addListener('drag', () => this.redrawCircles());
    this.marker.addListener('dragend', () => {
      const pos = this.markerPosition();
      if (pos) {
        this.applyPosition(pos.lat, pos.lng);
      }
    });
    this.map.addListener('click', (event: any) => {
      const lat2 = event.latLng.lat();
      const lng2 = event.latLng.lng();
      this.moveMarker(lat2, lng2);
      this.applyPosition(lat2, lng2);
    });

    this.valueSub = this.point.valueChanges.subscribe(() => {
      this.syncMarkerFromForm();
      this.redrawCircles();
    });

    if (!hasCoords) {
      this.centreOnUserLocation();
    }
    this.isLoading = false;
  }

  private initMap(): void {
    const L = (window as any).L;
    if (!L || !this.mapHost?.nativeElement) {
      this.isLoading = false;
      return;
    }

    // Bundled builds rewrite the marker image paths; pinning them to the CDN
    // keeps the pin from rendering as a broken image.
    L.Icon.Default.imagePath = LEAFLET_IMAGES;

    const lat = this.point.get('lat')?.value;
    const lng = this.point.get('lng')?.value;
    const hasCoords = lat != null && lng != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng));
    const centre: [number, number] = hasCoords
      ? [Number(lat), Number(lng)]
      : [FALLBACK.lat, FALLBACK.lng];

    this.map = L.map(this.mapHost.nativeElement, {
      zoomControl: true,
      attributionControl: true,
      // The admin portal creates its map at 20 and its layers go to 22; the old
      // cap of 19 is why the admin could zoom closer to a shopfront than we could.
      maxZoom: 20,
    }).setView(centre, hasCoords ? 18 : FALLBACK.zoom);

    // Google raster tiles — the labelled-POI look the admin portal uses.
    // lyrs=m streets · s satellite · y hybrid (labels over imagery) · p terrain
    const googleLayer = (lyrs: string) =>
      L.tileLayer(`https://mt1.google.com/vt/lyrs=${lyrs}&x={x}&y={y}&z={z}`, {
        attribution: '&copy; Google Maps',
        maxZoom: 22,
      });

    this.streetLayer = googleLayer('m');
    this.satelliteLayer = googleLayer('s');
    this.hybridLayer = googleLayer('y');
    this.terrainLayer = googleLayer('p');

    this.streetLayer.addTo(this.map);

    // mt1.google.com/vt is undocumented and can start refusing requests without
    // warning. Swap in OSM on the first tile error so the author gets a usable
    // map instead of a grey box.
    this.streetLayer.on('tileerror', () => {
      if (this.usedTileFallback) {
        return;
      }
      this.usedTileFallback = true;
      this.map.removeLayer(this.streetLayer);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '© OpenStreetMap contributors',
      }).addTo(this.map);
    });

    L.control
      .layers(
        {
          Streets: this.streetLayer,
          Satellite: this.satelliteLayer,
          Hybrid: this.hybridLayer,
          Terrain: this.terrainLayer,
        },
        {},
        { position: 'topright' }
      )
      .addTo(this.map);

    // The entry circle is drawn first so the tighter prize circle sits on top of
    // it rather than being hidden underneath.
    this.radiusCircle = L.circle(centre, {
      color: '#5304fb', weight: 3, fillColor: '#5304fb', fillOpacity: 0.18, radius: 1,
    }).addTo(this.map);
    this.prizeCircle = L.circle(centre, {
      color: '#f59e0b', weight: 2, dashArray: '5 4', fillColor: '#f59e0b', fillOpacity: 0.25, radius: 1,
    }).addTo(this.map);
    this.marker = L.marker(centre, { draggable: true }).addTo(this.map);
    this.redrawCircles();

    this.marker.on('drag', () => this.redrawCircles());
    this.marker.on('dragend', () => {
      const pos = this.marker.getLatLng();
      this.applyPosition(pos.lat, pos.lng);
    });
    this.map.on('click', (event: any) => {
      this.marker.setLatLng(event.latlng);
      this.applyPosition(event.latlng.lat, event.latlng.lng);
    });

    // The coordinates and both radii are also editable from the number inputs
    // below the map, so mirror the form back onto the map rather than assuming
    // the marker is the only thing that ever moves the point.
    this.valueSub = this.point.valueChanges.subscribe(() => {
      this.syncMarkerFromForm();
      this.redrawCircles();
    });

    // The picker often renders inside a collapsed/animating card, where Leaflet
    // measures zero and paints grey tiles until told to re-measure.
    // invalidateSize stays — the picker renders inside an animating card and
     // Leaflet would otherwise keep the zero width it measured on create. The map
     // is deliberately NOT fitted to the radius: a flat z18 keeps the surrounding
     // streets and shops in view, which is how the admin portal frames a point.
    setTimeout(() => this.map?.invalidateSize(), 250);
    if (!hasCoords) {
      this.centreOnUserLocation();
    }
    this.isLoading = false;
  }

  // Without this the map keeps whatever zoom it was created at, so a 15 m circle
  // renders as a blob smaller than the marker pin, and on the zoomed-out fallback
  // view it is sub-pixel — which reads as "the radius circle isn't showing".
  private fitToRadius(): void {
    if (!this.map || !this.radiusCircle) {
      return;
    }
    if (this.isGoogle) {
      // Google has no pad(); the circle bounds alone sit flush against the edges,
      // so nudge the zoom out one step to leave the same breathing room.
      this.map.fitBounds(this.radiusCircle.getBounds());
      const zoom = this.map.getZoom?.();
      if (typeof zoom === 'number' && zoom > 1) {
        this.map.setZoom(Math.min(zoom - 1, 20));
      }
      return;
    }
    this.map.fitBounds(this.radiusCircle.getBounds().pad(0.35), { maxZoom: 20, animate: false });
  }

  // Typing coordinates into the inputs must move the pin too, otherwise the
  // circles stay drawn around the marker's old position — often off-screen.
  private syncMarkerFromForm(): void {
    const lat = Number(this.point.get('lat')?.value);
    const lng = Number(this.point.get('lng')?.value);
    if (!this.marker || Number.isNaN(lat) || Number.isNaN(lng)) {
      return;
    }
    const current = this.markerPosition();
    // Guard against the feedback loop: applyPosition writes the same values back.
    if (current && Math.abs(current.lat - lat) < 1e-6 && Math.abs(current.lng - lng) < 1e-6) {
      return;
    }
    this.moveMarker(lat, lng);
    this.centreMap(lat, lng);
  }

  private redrawCircles(): void {
    const centre = this.markerPosition();
    if (!centre || !this.radiusCircle || !this.prizeCircle) {
      return;
    }
    if (this.isGoogle) {
      this.radiusCircle.setCenter(centre);
      this.prizeCircle.setCenter(centre);
    } else {
      this.radiusCircle.setLatLng([centre.lat, centre.lng]);
      this.prizeCircle.setLatLng([centre.lat, centre.lng]);
    }
    this.radiusCircle.setRadius(Math.max(this.radiusValue, 1));
    this.prizeCircle.setRadius(Math.max(this.prizeRadiusValue, 1));
  }

  private applyPosition(lat: number, lng: number, keepExistingName = false): void {
    // 6 dp is roughly 0.1 m — finer than the radius gate can ever resolve.
    this.point.get('lat')?.setValue(Number(lat.toFixed(6)));
    this.point.get('lng')?.setValue(Number(lng.toFixed(6)));
    this.point.get('lat')?.markAsTouched();
    this.point.get('lng')?.markAsTouched();

    const link = this.point.get('google_maps_link');
    const current = link?.value || '';
    // Never clobber a link the admin pasted by hand.
    if (!current || /^https:\/\/www\.google\.com\/maps\?q=/.test(current)) {
      link?.setValue(`https://www.google.com/maps?q=${lat},${lng}`);
    }

    this.redrawCircles();
    this.reverseGeocode(lat, lng, keepExistingName);
  }

  // Nominatim needs no key. Failure is silent — the address is a convenience and
  // the coordinates the admin just picked are already stored.
  private reverseGeocode(lat: number, lng: number, keepExistingName = false): void {
    // A point seeded from the author's own position may already carry an authored
    // location_name (levels saved as "Play Anywhere" keep the name but no
    // coordinates). Overwriting it with wherever the author happens to be sitting
    // would destroy real content, so only fill a name that is actually missing.
    if (keepExistingName && (this.point.get('location_name')?.value || '').trim()) {
      return;
    }
    this.isGeocoding = true;

    // Google's reverse geocoder returns the same place names shown on its tiles,
    // so the address matches what the author can actually see on the map.
    if (this.isGoogle && this.geocoder) {
      this.geocoder.geocode({ location: { lat, lng } }, (results: any[], status: string) => {
        if (status === 'OK' && results?.length) {
          this.point.get('location_name')?.setValue(results[0].formatted_address);
        }
        this.isGeocoding = false;
      });
      return;
    }

    fetch(`https://nominatim.openstreetmap.org/reverse?format=json&zoom=18&lat=${lat}&lon=${lng}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.display_name) {
          this.point.get('location_name')?.setValue(data.display_name);
        }
      })
      .catch(() => undefined)
      .finally(() => (this.isGeocoding = false));
  }

  onSearchInput(term: string): void {
    this.searchTerm = term;
    this.searchError = '';
    if (!term.trim()) {
      this.results = [];
      return;
    }

    // A pasted coordinate pair or Google Maps URL is an exact answer — jump to it
    // rather than sending it to a place-name search that will never match it.
    const parsed = this.parseGeoInput(term);
    if (parsed) {
      this.goToCoordinates(parsed.lat, parsed.lng);
      return;
    }

    // A short maps link carries no coordinates until it is followed, so wait for
    // Enter/paste (onSearchCommit) instead of resolving on every keystroke.
    if (this.looksLikeShortMapsUrl(term)) {
      this.results = [];
      return;
    }

    this.search$.next(term.trim());
  }

  /** The paste event fires before the pasted text reaches the input, so the
   *  commit has to wait a tick for `searchTerm` to catch up. */
  onSearchPaste(): void {
    setTimeout(() => void this.onSearchCommit(), 60);
  }

  /** Enter or paste — the point at which resolving a short maps URL is worth a
   *  network round-trip. Plain place names fall through to the debounced search. */
  async onSearchCommit(): Promise<void> {
    const term = this.searchTerm.trim();
    if (!term) {
      return;
    }

    const parsed = this.parseGeoInput(term);
    if (parsed) {
      this.goToCoordinates(parsed.lat, parsed.lng);
      return;
    }

    if (!this.looksLikeShortMapsUrl(term)) {
      return;
    }

    this.isResolvingUrl = true;
    this.searchError = '';
    try {
      const resolved = await this.resolveShortMapsUrl(term);
      if (resolved) {
        this.goToCoordinates(resolved.lat, resolved.lng);
      } else {
        this.searchError = 'geo.map.urlParseFailed';
      }
    } catch {
      this.searchError = 'geo.map.urlParseFailed';
    } finally {
      this.isResolvingUrl = false;
    }
  }

  private goToCoordinates(lat: number, lng: number): void {
    this.results = [];
    this.searchTerm = '';
    this.searchError = '';
    this.moveMarker(lat, lng);
    this.centreMap(lat, lng, 18);
    this.applyPosition(lat, lng);
  }

  private looksLikeShortMapsUrl(value: string): boolean {
    const trimmed = value.trim();
    return (
      /^https?:\/\//i.test(trimmed) &&
      /(google\.[^/]+\/maps|maps\.app\.goo\.gl|goo\.gl\/maps|g\.co\/kgs|maps\.google\.[^/]+)/i.test(trimmed)
    );
  }

  /** Accepts a Google Maps URL in any of its shapes, or a plain "lat, lng" /
   *  "lat lng" pair. Returns null when nothing valid is in the string. */
  parseGeoInput(value: string): { lat: number; lng: number } | null {
    const normalize = (lat: number, lng: number) =>
      Number.isFinite(lat) && Number.isFinite(lng) &&
      lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
        ? { lat, lng }
        : null;

    const parsePair = (raw: string | null | undefined) => {
      if (!raw) return null;
      const cleaned = decodeURIComponent(raw).replace(/\+/g, ' ').trim();
      const match = cleaned.match(/(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/);
      return match ? normalize(parseFloat(match[1]), parseFloat(match[2])) : null;
    };

    const trimmed = value.trim();
    let decoded = trimmed;
    try {
      decoded = decodeURIComponent(trimmed);
    } catch {
      // Malformed percent-encoding — keep the raw string.
    }

    // Modern maps links put the coordinates in a query parameter.
    try {
      const url = new URL(trimmed);
      for (const key of ['q', 'query', 'destination', 'origin', 'll', 'center']) {
        const coords = parsePair(url.searchParams.get(key));
        if (coords) return coords;
      }
    } catch {
      // Not a full URL — fall through to the pattern scan.
    }

    const patterns = [
      /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
      /place\/[^/]*\/(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
      /[?&]q=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
      /[?&]query=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
      /[?&]destination=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
      /[?&]center=(-?\d+(?:\.\d+)?)(?:,|%2C)(-?\d+(?:\.\d+)?)/,
      /[?&]ll=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
      /dir\/(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
      /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/,
    ];

    for (const source of [trimmed, decoded]) {
      for (const pattern of patterns) {
        const match = source.match(pattern);
        if (match) {
          const coords = normalize(parseFloat(match[1]), parseFloat(match[2]));
          if (coords) return coords;
        }
      }
    }

    const pair = parsePair(trimmed);
    if (pair) return pair;

    // "24.7136 46.6753"
    const parts = trimmed.split(/\s+/).map(Number);
    if (parts.length >= 2 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1])) {
      return normalize(parts[0], parts[1]);
    }

    return null;
  }

  /** A maps.app.goo.gl link carries no coordinates until the redirect is
   *  followed, which the browser cannot do cross-origin — proxy it, then parse
   *  the final URL and the returned HTML. */
  private async resolveShortMapsUrl(shortUrl: string): Promise<{ lat: number; lng: number } | null> {
    const fetchThrough = async (url: string) => {
      const res = await fetch(`https://api.allorigins.win/get?url=${encodeURIComponent(url)}`);
      if (!res.ok) return null;
      return res.json();
    };

    const extractFrom = (data: any): { lat: number; lng: number } | null => {
      const content: string = data?.contents || '';
      const finalUrl: string = data?.status?.url || '';
      return this.parseGeoInput(finalUrl) || this.parseGeoInput(content);
    };

    const data = await fetchThrough(shortUrl);
    if (!data) return null;

    const direct = extractFrom(data);
    if (direct) return direct;

    const content: string = data?.contents || '';
    const redirect =
      content.match(/content="(https?:\/\/[^"]*google[^"]*maps[^"]*)"/)?.[1] ||
      content.match(/url=(https?:\/\/[^"'\s>]+)/i)?.[1] ||
      content.match(/window\.location\s*=\s*["'](https?:\/\/[^"']+)/i)?.[1] ||
      content.match(/href="(https?:\/\/www\.google\.com\/maps[^"]+)"/i)?.[1];

    if (!redirect) return null;

    const fromRedirectUrl = this.parseGeoInput(decodeURIComponent(redirect));
    if (fromRedirectUrl) return fromRedirectUrl;

    const followed = await fetchThrough(redirect);
    return followed ? extractFrom(followed) : null;
  }

  private runSearch(term: string): void {
    this.isSearching = true;

    // Places autocomplete knows the local businesses OSM has never heard of,
    // which is the whole reason for preferring Google when a key exists.
    const google = (window as any).google;
    if (this.isGoogle && google?.maps?.places) {
      const service = new google.maps.places.PlacesService(this.map);
      service.textSearch({ query: term }, (places: any[], status: string) => {
        this.results =
          status === 'OK' && places?.length
            ? places.slice(0, 5).map((place) => ({
              label: place.formatted_address
                ? `${place.name} — ${place.formatted_address}`
                : place.name,
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            }))
            : [];
        this.isSearching = false;
      });
      return;
    }

    fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(term)}`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data: any[]) => {
        this.results = (data || []).map((item) => ({
          label: item.display_name,
          lat: Number(item.lat),
          lng: Number(item.lon),
        }));
      })
      .catch(() => (this.results = []))
      .finally(() => (this.isSearching = false));
  }

  selectResult(result: PlaceResult): void {
    this.results = [];
    this.searchTerm = '';
    this.moveMarker(result.lat, result.lng);
    this.centreMap(result.lat, result.lng, 18);
    // The search result already carries the address, so set it directly instead
    // of spending a second Nominatim call reverse-geocoding the same spot.
    this.point.get('lat')?.setValue(Number(result.lat.toFixed(6)));
    this.point.get('lng')?.setValue(Number(result.lng.toFixed(6)));
    this.point.get('location_name')?.setValue(result.label);
    const link = this.point.get('google_maps_link');
    const current = link?.value || '';
    if (!current || /^https:\/\/www\.google\.com\/maps\?q=/.test(current)) {
      link?.setValue(`https://www.google.com/maps?q=${result.lat},${result.lng}`);
    }
    this.redrawCircles();
  }

  onRadiusSlide(value: number): void {
    this.point.get('radius')?.setValue(Number(value));
    this.redrawCircles();
  }

  /** Frames the whole entry circle. Deliberately manual: automatic fitting on
   *  load or on slider release moved the map under the author while they were
   *  tuning the radius. */
  fitToRadiusNow(): void {
    this.fitToRadius();
  }

  /** Only the (currently unused) Google engine still needs this — the Leaflet
   *  path switches base layers through Leaflet's own L.control.layers, which
   *  reaches all four layers rather than flipping between two. */
  toggleLayer(): void {
    if (!this.map || !this.isGoogle) {
      return;
    }
    this.isSatellite = !this.isSatellite;
    // hybrid keeps the labels over the imagery; plain satellite drops them.
    this.map.setMapTypeId(this.isSatellite ? 'hybrid' : 'roadmap');
  }

  toggleFullscreen(): void {
    const shell = this.mapShell?.nativeElement;
    if (!shell) {
      return;
    }
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void shell.requestFullscreen?.();
    }
  }

  locateMe(): void {
    if (!navigator.geolocation) {
      return;
    }
    this.isLocating = true;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        this.moveMarker(latitude, longitude);
        this.centreMap(latitude, longitude, 18);
        this.applyPosition(latitude, longitude);
        this.isLocating = false;
      },
      () => (this.isLocating = false),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  }

  /** A point with no coordinates would otherwise open on the Riyadh fallback at
   *  country zoom. Seed it from the author's own position instead: the pin moves
   *  there and the coordinates are written to the form, so the point is usable
   *  straight away and the author only has to nudge it. An authored
   *  location_name is preserved — only a missing one is filled in. */
  private centreOnUserLocation(): void {
    if (!navigator.geolocation || typeof window === 'undefined' || !window.isSecureContext) {
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Bail out if the author already picked a spot while we were waiting.
        if (this.point.get('lat')?.value != null && this.point.get('lng')?.value != null) {
          return;
        }
        const { latitude, longitude } = position.coords;
        this.moveMarker(latitude, longitude);
        this.centreMap(latitude, longitude, 18);
        this.applyPosition(latitude, longitude, true);
      },
      () => undefined,
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  }
}
